import type { IArticleConfig } from '~/config/article';
import widgetsConfig from '~/config/widgets.config';
import Iframe from './Iframe';
import ServerLogger from '~/modules/ServerLogger';
import debounce from 'just-debounce-it';
import Session from '~/modules/Session';

const isSurveyQueryParamPresent = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get('surveyId') && !window.location.href.includes('quizzly.ai');
};

const decideSponsoredOnly = (sponsoredOnly: boolean) => {
  return isSurveyQueryParamPresent() || sponsoredOnly;
};

const getSurveyIdFromQueryParam = (forcedSurveyId?: string | null) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return forcedSurveyId || urlSearchParams.get('surveyId');
};

const areSurveyAdsDisabled = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get('isExposedGroup') === 'false';
};

class QuizIframe extends Iframe {
  constructor(
    readonly id: string,
    readonly config: IArticleConfig
  ) {
    /** ‼ ⚠ Call parent constructor (Iframe class) */
    super(
      id,
      'Vuukle Quiz Widget',
      widgetsConfig.urls.quiz,
      {
        apiKey: config.apiKey,
        host: config.host,
        articleId: config.articleId,
        tags: config.tags,
        title: config.title,
        url: config.url,
        img: config.img,
        version: widgetsConfig.versions.quiz,
        language: config.language || 'en',
        sponsoredOnly: decideSponsoredOnly(Boolean(config.quiz.sponsoredOnlyQuestions)),
        sponsoredQuestionPosition:
          typeof config.quiz.sponsoredQuestionsPosition === 'number' &&
          Math.max(1, config.quiz.sponsoredQuestionsPosition),
        forcedSponsoredSurveyId: getSurveyIdFromQueryParam(config.quiz.forcedSponsoredSurveyId),
        forceExposedGroup: config.quiz.forceExposedGroup || isSurveyQueryParamPresent(),
        disableSurveyAds: areSurveyAdsDisabled(),
      },
      false,
      false
    );
    this.iframe.style.maxHeight = '265px';
    this.iframe.allow = 'web-share; clipboard-write';
    this.intersectionObserverOptions = {
      threshold: 0,
      rootMargin: '400px 400px 400px 400px',
    };

    if (config.quiz.compactMode && !Session.isMobileUser) {
      this.onLoad((iframeWindow) => iframeWindow.postMessage({ compactMode: true }, '*'));
      this.changeHeight(250);
      this.changeWidth(250);
    }

    if (config.quiz.quizzlyExploreRedirect) {
      this.onLoad((iframeWindow) => iframeWindow.postMessage({ redirectToExplore: true }, '*'));
    }

    /**
     * When #quizzly is present in the URL, scroll down to Quiz widget
     */
    if (window.location.hash && window.location.hash.match(/quizzly/)) {
      if (document.readyState === 'interactive' || document.readyState == 'complete') {
        setTimeout(() => {
          const iframeRect = this.iframe.getBoundingClientRect();
          window.scrollTo(iframeRect.x, iframeRect.y);
        }, 1500);
      } else {
        const iframe = this.iframe;
        /**
         *
         */
        function onReadyStateChange() {
          if (document.readyState === 'interactive' || document.readyState === 'complete') {
            setTimeout(() => {
              const iframeRect = iframe.getBoundingClientRect();
              window.scrollTo(iframeRect.x, iframeRect.y);
              document.removeEventListener('readystatechange', onReadyStateChange);
            }, 1500);
          }
        }
        document.addEventListener('readystatechange', onReadyStateChange);
      }
    }
  }

  public changeHeight(height: number | string) {
    this.iframe.style.maxHeight = typeof height === 'string' ? height : `${height}px`;
  }

  public changeWidth(width: number | string) {
    this.iframe.style.maxWidth = typeof width === 'string' ? width : `${width}px`;
    this.iframe.style.width = typeof width === 'string' ? width : `${width}px`;
  }

  private serverScrollLogger = (isAMPArticle: boolean) => {
    // We need to log message instantly as we don't have access to JS things inside AMP articles
    if (isAMPArticle) {
      ServerLogger.log('view_quizzly', this.config);
      this.onLoad((iframeWindow) => iframeWindow.postMessage({ isViewed: true }, '*'));
      return () => false;
    }
    // Otherwise create listener to sent logs only when needed
    let viewLogSent = false;
    return debounce((inView: boolean) => {
      const inViewNoLogSent = inView && !viewLogSent;

      if (inViewNoLogSent) {
        ServerLogger.log('view_quizzly', this.config);
        this.onLoad((iframeWindow) => iframeWindow.postMessage({ isViewed: true }, '*'));
        viewLogSent = true;
      }
    }, 300);
  };

  public fireQuizViewEvent() {
    const serverScrollLogger = this.serverScrollLogger(this.config.amp || false);
    if (typeof IntersectionObserver !== 'undefined') {
      const quizObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            const { intersectionRatio, isIntersecting } = entry;
            const isInView = intersectionRatio > 0.025 || isIntersecting;
            scheduler.postTask(
              () => {
                serverScrollLogger(isInView);
              },
              { priority: 'background' }
            );
          });
        },
        { threshold: 0.1 }
      );
      quizObserver.observe(this.iframe);
    } else {
      window.addEventListener(
        'scroll',
        () => {
          scheduler.postTask(
            () => {
              serverScrollLogger(this.isInView);
            },
            { priority: 'background' }
          );
        },
        false
      );
    }
  }
}

export default QuizIframe;
